import {AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation} from '@angular/core';
import {Subject, filter, take, takeUntil} from 'rxjs';
import {UserElement, UserService, UserRoles} from '../../shared/services/user.service';
import {HcModal, HcSort, HcTableDataSource, ModalOptions, ModalService, PaginationComponent, SimpleModalOptions} from '@healthcatalyst/cashmere';
import {FormControl, FormGroup, Validators} from '@angular/forms';

/** Admin user management */
@Component({
    selector: 'pd-admin-users',
    templateUrl: './admin-users.component.html',
    styleUrls: ['./admin-users.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminUsersComponent implements OnInit, AfterViewInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    dataSource: HcTableDataSource<UserElement> = new HcTableDataSource();
    loading = true;
    loadError = false;
    adminUsersDisplayedColumns: string[] = ['name', 'okta_id', 'role', 'actions'];
    adminUsersPageSize: number = 10;
    adminUsersPageSizeOptions: number[] = [2, 4, 6];
    private modalRef: HcModal<TemplateRef<any>>;
    adminUserControl = new FormGroup({
        name: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
        okta_id: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
        id: new FormControl(0, {nonNullable: true}),
        role: new FormControl( UserRoles.Admin, {nonNullable: true})
    });

    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    get adminUsersListLength(): number {
        return this.dataSource.filteredData ? this.dataSource.filteredData.length : 0;
    }

    constructor(private userService: UserService, private modalService: ModalService) {}

    ngOnInit(): void {
        this.userService.usersUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.loading = false;
                this.loadError = false;
                this.dataSource = new HcTableDataSource(list);
                if ( this.sort && this.paginator ) {
                    this.dataSource.sort = this.sort;
                    this.dataSource.paginator = this.paginator;
                }
            } else {
                this.loadError = this.userService.loadFailed;
                if (!this.loadError) {
                    this.loadUsers();
                } else {
                    this.loading = false;
                }
            }
        });
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    loadUsers(): void {
        this.userService.refresh();
    }

    applyFilter(filter: string): void {
        this.dataSource.filter = filter.trim().toLowerCase();
    }

    displayRole( role: string ): string {
        return role === 'admin' ? 'Admin' : 'Super Admin';
    }

    openAddEditModal(content: TemplateRef<any>, edit: boolean, user?: UserElement) {
        const options: ModalOptions = {
            size: 'md',
            data: {
                user: user,
                edit: edit
            }
        };
        this.adminUserControl.reset();

        if(user) {
            this.adminUserControl.patchValue({
                id: user.id,
                name: user.name,
                okta_id: user.okta_id,
                role: user.role        
             });
        } 
        this.modalRef = this.modalService.open(content, options);
    }

    editUser() {
        const target: UserElement = {
            name: this.adminUserControl.controls['name'].value,
            id: this.adminUserControl.controls['id'].value,
            okta_id: this.adminUserControl.controls['okta_id'].value,
            role: this.adminUserControl.controls['role'].value
        };
        this.userService.update( target );
        this.modalRef.close();
    }

    addUser() {
        const target: UserElement = {
            name: this.adminUserControl.controls['name'].value,
            id: -1,
            okta_id: this.adminUserControl.controls['okta_id'].value,
            role: this.adminUserControl.controls['role'].value
        };
        this.userService.add( target );
        this.modalRef.close();
    }

    removeUser( element: UserElement) {
        const options: SimpleModalOptions = {
            messageHTML: '<p>Are you sure you want to remove the user: <strong>' + element.name + '</strong>?<p>',
            headerText: 'Delete User',
            confirmButtonText: 'Delete User',
            icon: ''
        };
        this.modalService.confirmDestructive(options)
            .pipe(take(1),filter(confirmed => confirmed))
            .subscribe(() => {
                this.userService.remove( element );
            });
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
